<template>
  <div ref="root" class="health-certificates" :class="{ compact }">
    <div class="section-header">
      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
      <h3>{{ title }}</h3>
    </div>

    <div class="cert-stats">
      <div class="stat-item">
        <span class="stat-label">总计</span>
        <span class="stat-value">{{ stats.total }}</span>
      </div>
      <div class="stat-item stat-normal">
        <span class="stat-label">正常</span>
        <span class="stat-value">{{ stats.normal }}</span>
      </div>
      <div class="stat-item stat-warning">
        <span class="stat-label">临期</span>
        <span class="stat-value">{{ stats.expiring }}</span>
      </div>
      <div class="stat-item stat-danger">
        <span class="stat-label">过期</span>
        <span class="stat-value">{{ stats.expired }}</span>
      </div>
    </div>

    <div ref="scrollContainer" class="cert-list">
      <div class="cert-grid" :style="gridStyle">
        <div
          v-for="(cert, index) in scrollList"
          :key="index"
          class="cert-item"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="cert-info">
            <div class="cert-title">
              <span class="cert-name" :title="cert.name">{{ cert.name }}</span>
              <span class="cert-status" :class="statusClass(cert.status)">
                <span class="status-dot"></span>
                <span class="status-text">{{ cert.status }}</span>
              </span>
            </div>
            <div class="cert-expiry">有效期：{{ cert.expiry_date || '-' }}</div>
          </div>
          <img :src="cert.img_url" class="cert-image" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HealthCertificates',
  props: {
    title: {
      type: String,
      default: '健康证展示'
    },
    certificates: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      columns: 1,
      compact: false,
      resizeObserver: null
    }
  },
  computed: {
    stats() {
      const total = this.certificates.length
      const normal = this.certificates.filter(c => c.status === '正常').length
      const expiring = this.certificates.filter(c => c.status === '临期').length
      const expired = this.certificates.filter(c => c.status === '过期').length
      return { total, normal, expiring, expired }
    },
    scrollList() {
      return [...this.certificates, ...this.certificates]
    },
    gridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`
      }
    }
  },
  mounted() {
    this.updateColumns()
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updateColumns)
      this.resizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.updateColumns)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updateColumns)
    }
    if (this.scrollTimer) {
      clearInterval(this.scrollTimer)
    }
  },
  methods: {
    updateColumns() {
      const width = this.$el ? this.$el.clientWidth : 0
      this.compact = width < 420
      this.columns = width >= 420 ? 2 : 1
    },
    statusClass(status) {
      if (status === '正常') return 'status-normal'
      if (status === '临期') return 'status-warning'
      if (status === '过期') return 'status-danger'
      return ''
    },
    startAutoScroll() {
      const container = this.$refs.scrollContainer
      if (!container) return

      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      if (scrollHeight <= clientHeight) return

      const halfHeight = scrollHeight / 2
      let scrollTop = 0

      const scroll = () => {
        scrollTop += 1
        if (scrollTop >= halfHeight) {
          container.scrollTop = 0
          scrollTop = 0
        }
        container.scrollTop = scrollTop
      }

      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
      }
      this.scrollTimer = setInterval(scroll, 30)
    }
  }
}
</script>

<style scoped>
.health-certificates {
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

.icon {
  width: 20px;
  height: 20px;
  color: #63b3ed;
}

h3 {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

/* ==================== 统计数据 ==================== */
.cert-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.12);
  flex-shrink: 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 4px;
  background: rgba(49, 130, 206, 0.1);
  border-radius: 8px;
  min-width: 0;
}

.stat-label {
  font-size: 12px;
  color: #a0aec0;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #63b3ed;
}

.stat-normal .stat-value {
  color: #48bb78;
}

.stat-warning .stat-value {
  color: #f6ad55;
}

.stat-danger .stat-value {
  color: #fc8181;
}

.health-certificates.compact .cert-stats {
  gap: 4px;
  padding-bottom: 8px;
}

.health-certificates.compact .stat-item {
  padding: 4px 2px;
}

.health-certificates.compact .stat-label {
  font-size: 11px;
}

.health-certificates.compact .stat-value {
  font-size: 16px;
}

/* ==================== 健康证列表 ==================== */
.cert-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 12px;
}

.cert-grid {
  display: grid;
  gap: 8px;
}

.cert-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 8px 10px;
  background: rgba(49, 130, 206, 0.1);
  border-radius: 8px;
  animation: fadeIn 0.5s ease-out forwards;
  opacity: 0;
}

.cert-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cert-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-width: 0;
}

.cert-name {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cert-expiry {
  font-size: 12px;
  color: #a0aec0;
  white-space: nowrap;
}

.cert-image {
  width: 76px;
  max-height: 60px;
  flex-shrink: 0;
  margin-left: auto;
  object-fit: contain;
  display: block;
  border-radius: 6px;
}

.cert-status {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-normal .status-dot {
  background: #48bb78;
}

.status-warning .status-dot {
  background: #f6ad55;
}

.status-danger .status-dot {
  background: #fc8181;
}

.status-text {
  font-size: 11px;
}

.status-normal .status-text {
  color: #48bb78;
}

.status-warning .status-text {
  color: #f6ad55;
}

.status-danger .status-text {
  color: #fc8181;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
